import { getCookie } from 'cookies-next';
import { Content } from 'pleinchamp-api-client';
import { createContext } from 'react';
import { usePlcApiCall } from '@api/business/api.hook';
import { useLocation } from '@meteo/business/LocationSelector.hook';
import { useSelectNewCity } from '@meteo/business/meteo.utils';
import { City, StandardCity } from '@meteo/types';
import { fetchServicesNews } from '@services/business/Services.api';
import { TreatmentSchedulePageLayout } from '@services/components/TreatmentSchedulePageLayout/TreatmentSchedulePageLayout.component';
import { defaultNamespaces } from '@utils/strings';
import { PlcNextPage, PlcNextPageContext, PlcPageContextProps } from 'features/business/app.types';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

interface Props {
  news: Content[];
  lastMeteoCity?: City;
}

type CityContextProp = {
  data?: StandardCity;
  isValidating: boolean;
  error?: string;
  setData: (data?: City) => void;
};

export const TreatmentScheduleContext = createContext<PlcPageContextProps<Props> & { city: CityContextProp }>({
  city: { isValidating: false, setData: () => {} },
  lastMeteoCity: undefined,
  news: { isValidating: false },
});

const TreatmentSchedulePage: PlcNextPage<Partial<Props>> = (props) => {
  const { selectedCity, onSelectNewCity } = useSelectNewCity(undefined, props.lastMeteoCity);
  const { city, isGeolocationLoading, geolocationError } = useLocation(selectedCity);
  const news = usePlcApiCall(fetchServicesNews, [], {
    initialData: props.news,
  });

  return (
    <TreatmentScheduleContext.Provider
      value={{
        city: {
          data: city,
          error: geolocationError,
          isValidating: isGeolocationLoading,
          setData: onSelectNewCity,
        },
        news,
      }}
    >
      <TreatmentSchedulePageLayout />
    </TreatmentScheduleContext.Provider>
  );
};

export async function getServerSideProps(ctx: PlcNextPageContext) {
  const lastMeteoCityString = await getCookie('lastMeteoCity', ctx);
  const lastMeteoCity = lastMeteoCityString ? JSON.parse(lastMeteoCityString) : null;
  return {
    props: {
      ...(await serverSideTranslations(ctx.locale || 'fr', defaultNamespaces)),
      lastMeteoCity,
    },
  };
}
export default TreatmentSchedulePage;
